import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal as ModalMUI } from '@mui/material';
import React from 'react';
import styled, { css } from 'styled-components';
import useTracking, { TrackingEventData } from '../hooks/useTracking';
import { BACKGROUND, BORDER_GRAY, WHITE } from '../theme';
import { EventNames } from '../utils/trackingEvents';
import Button, { ButtonProps, ButtonVariant } from './reusable/Button';
import { IconButton } from './reusable/Button/IconButton';

const StyledModalHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 4px;

  padding: 16px;
  border-bottom: 1px solid ${BORDER_GRAY};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const HeaderTitles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

/* Keeps anything sharing the top-right corner on one centred line with the X. */
const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const MODAL_CONTENT_BASE_STYLE = css`
  max-height: 70vh;
  padding: 16px 16px 40px 16px;

  background-color: ${BACKGROUND};

  display: flex;
  flex-direction: column;

  overflow-y: auto;
  box-sizing: border-box;
`;

interface ModalHeaderProps {
  testId?: string;
  children: React.ReactNode;
  /** When provided, the header renders a close (X) button on its right. */
  onClose?: () => void;
  closeTestId?: string;
  /** Accessible name for the close button, e.g. "Close booking". */
  closeLabel?: string;
  closeTrackingData?: TrackingEventData;
  /**
   * Rendered in the top-right corner, on the same line as the close button -
   * for a status badge or similar adornment that belongs to the modal rather
   * than to its title.
   */
  rightContent?: React.ReactNode;
}

const ModalHeader = ({
  testId = 'modal-header',
  children,
  onClose,
  closeTestId,
  closeLabel = 'Close',
  closeTrackingData,
  rightContent,
}: ModalHeaderProps) => (
  <StyledModalHeader data-testid={testId}>
    {onClose || rightContent ? (
      <HeaderRow>
        <HeaderTitles>{children}</HeaderTitles>
        <HeaderControls>
          {rightContent}
          {onClose && (
            <IconButton
              ariaLabel={closeLabel}
              onClick={onClose}
              icon={<FontAwesomeIcon icon={faXmark} fontSize={16} />}
              testId={closeTestId ?? `${testId}-close`}
              variant={ButtonVariant.Transparent}
              trackingData={closeTrackingData}
            />
          )}
        </HeaderControls>
      </HeaderRow>
    ) : (
      children
    )}
  </StyledModalHeader>
);

const StyleModalContent = styled.div`
  ${MODAL_CONTENT_BASE_STYLE}

  gap: 16px;

  overflow-y: auto;
`;

const ModalContent = ({
  testId = 'modal-content',
  children,
  style,
}: {
  testId?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <StyleModalContent style={style} data-testid={testId}>
    {children}
  </StyleModalContent>
);

const StyledModalFooter = styled.footer`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px;
  height: 56px;
  background: ${WHITE};
  border-top: 1px solid ${BORDER_GRAY};
  border-radius: 0 0 8px 8px;

  box-sizing: border-box;
`;

const ModalFooter = ({
  testId = 'modal-footer',
  children,
}: {
  testId?: string;
  children: React.ReactNode;
}) => <StyledModalFooter data-testid={testId}>{children}</StyledModalFooter>;

interface ModalActionButton extends Omit<ButtonProps, 'children'> {
  label: React.ReactNode;
}

const LeftSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  /* Pushes everything after it to the right edge of the footer. */
  margin-right: auto;
`;

interface ModalActionsProps {
  /**
   * Pinned to the left edge - the Back step of a multi-step flow, or a
   * secondary/destructive action. Defaults to the outline variant.
   */
  secondaryButton?: ModalActionButton;
  /**
   * Pinned to the right edge - the modal's primary action. Defaults to the
   * dark variant.
   */
  confirmButton?: ModalActionButton;
  /** Extra left-aligned content, rendered before the secondary button. */
  leftContent?: React.ReactNode;
  /** Extra right-aligned content, rendered before the confirm button. */
  children?: React.ReactNode;
  testId?: string;
}

/**
 * The standard modal footer: a back/secondary action on the left, the primary
 * action on the right.
 *
 * Modals are dismissed with the header's X button (`<Modal.Header onClose />`),
 * so this footer deliberately has no Cancel slot.
 */
const ModalActions = ({
  secondaryButton,
  confirmButton,
  leftContent,
  children,
  testId = 'modal-footer',
}: ModalActionsProps) => {
  const hasLeftSlot = Boolean(leftContent || secondaryButton);
  const { label: secondaryLabel, ...secondaryProps } = secondaryButton ?? {};
  const { label: confirmLabel, ...confirmProps } = confirmButton ?? {};

  return (
    <StyledModalFooter data-testid={testId}>
      {hasLeftSlot && (
        <LeftSlot>
          {leftContent}
          {secondaryButton && (
            <Button variant={ButtonVariant.Outline} {...secondaryProps}>
              {secondaryLabel}
            </Button>
          )}
        </LeftSlot>
      )}
      {children}
      {confirmButton && (
        <Button variant={ButtonVariant.Dark} {...confirmProps}>
          {confirmLabel}
        </Button>
      )}
    </StyledModalFooter>
  );
};

interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  withPadding?: boolean;
  children?: React.ReactNode;
  testId?: string;
  width?: string;
  trackingData?: Partial<TrackingEventData>;
}

interface IModal extends React.FC<ModalProps> {
  Header: typeof ModalHeader;
  Content: typeof ModalContent;
  Footer: typeof ModalFooter;
  Actions: typeof ModalActions;
}

const Container = styled.div<{ $withPadding?: boolean; $width?: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${({ $width }) => $width || '550px'};
  background-color: ${WHITE};
  border-radius: 8px;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));

  border: 1px solid ${BORDER_GRAY};
  border-radius: 8px;

  box-sizing: border-box;

  ${({ $withPadding }) =>
    $withPadding
      ? css`
          padding: 16px 24px 8px;
        `
      : css`
          padding: 0;
        `}
`;

const Modal: IModal = ({
  isOpen,
  onClose,
  withPadding = false,
  children,
  testId,
  width,
  trackingData,
}) => {
  const { track } = useTracking();
  return (
    <ModalMUI
      open={isOpen}
      onClose={(_, reason) => {
        if (trackingData) {
          const eventName = {
            backdropClick: EventNames.ModalBackDropClick,
            escapeKeyDown: EventNames.ModalEscapeKeyPress,
          }[reason];

          track(eventName, trackingData);
        }

        onClose?.();
      }}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      data-testid={testId}
    >
      <Container $width={width} $withPadding={withPadding}>
        {children}
      </Container>
    </ModalMUI>
  );
};

Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;
Modal.Actions = ModalActions;

export default Modal;
